import { useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { BsCheckCircle, BsDashCircle } from 'react-icons/bs';

import Card from '#globalComponents/Card/Card';
import ConfirmModal from '#globalComponents/Overlay/ConfirmModal';
import { useErrorStore } from '#lib/stores/errorStore';

import { upsertChannelConfig } from '../../../api/crmApi';
import ChannelBadge from '../../../components/common/ChannelBadge/ChannelBadge';
import { channelsKeys } from '../../../models/channels/keys';
import { useChannelTypes, useChannelConfigs } from '../../../models/channels/queries';

/**
 * Lists available channels and lets admins toggle per-org activation.
 * Coming-soon channels are listed but disabled.
 */

const ChannelsSettingsTab = () => {
	const { data: channels = [], isLoading: typesLoading } = useChannelTypes(true);
	const { data: configs = [], isLoading: configsLoading } = useChannelConfigs();
	const qc = useQueryClient();
	const showError = useErrorStore((s) => s.showError);
	const [pendingId, setPendingId] = useState(null);
	const [disableTarget, setDisableTarget] = useState(null);

	const configByTypeId = new Map(configs.map((c) => [c.channelTypeId, c]));

	const handleToggle = async (channel, nextEnabled, confirmed = false) => {
		if (!nextEnabled && !confirmed) {
			setDisableTarget(channel);
			return;
		}
		setPendingId(channel.id);
		try {
			await upsertChannelConfig({ channelTypeId: channel.id, isEnabled: nextEnabled });
			qc.invalidateQueries({ queryKey: channelsKeys.all });
		} catch (err) {
			showError({ error: err, title: 'Kanal konnte nicht umgeschaltet werden' });
		} finally {
			setPendingId(null);
			setDisableTarget(null);
		}
	};

	if (typesLoading || configsLoading) return <p className="text-bgBase-800">Kanäle werden geladen …</p>;

	const active = channels.filter((c) => c.isActive);
	const stub = channels.filter((c) => !c.isActive);

	return (
		<div className="space-y-6">
			<div>
				<h2 className="text-sm font-semibold text-text mb-3">Verfügbare Kanäle</h2>
				<div className="space-y-2">
					{active.map((c) => {
						const cfg = configByTypeId.get(c.id);
						const isOrgEnabled = cfg ? cfg.isEnabled : c.isActive;
						return (
							<Card key={c.id} className="flex items-center justify-between">
								<div className="flex items-center gap-3">
									<ChannelBadge channelTypeId={c.id} />
									<div>
										<p className="text-sm font-medium text-text">{c.displayName}</p>
										<p className="text-xs text-bgBase-800">
											{c.executionMode === 'automated' ? 'Automatisiert' : 'Manuell'}
											{c.requiresAccount ? ' · Konto erforderlich' : ''}
										</p>
									</div>
								</div>
								<button
									onClick={() => handleToggle(c, !isOrgEnabled)}
									disabled={pendingId === c.id}
									className={`flex items-center gap-1 text-xs font-medium px-3 py-1.5 rounded-control border transition-colors ${
										isOrgEnabled
											? 'border-primary-300 bg-primary-100 text-primary-700'
											: 'border-bgBase-500 bg-white text-text hover:bg-bgBase-300'
									}`}
								>
									{isOrgEnabled ? (
										<>
											<BsCheckCircle className="w-3 h-3" /> Aktiviert
										</>
									) : (
										<>
											<BsDashCircle className="w-3 h-3" /> Deaktiviert
										</>
									)}
								</button>
							</Card>
						);
					})}
				</div>
			</div>

			{stub.length > 0 && (
				<div>
					<h2 className="text-sm font-semibold text-text mb-3">Coming soon</h2>
					<div className="space-y-2">
						{stub.map((c) => (
							<div
								key={c.id}
								className="flex items-center justify-between rounded-card border-2 border-dashed border-bgBase-500 bg-bgBase-300 p-4"
							>
								<div className="flex items-center gap-3">
									<ChannelBadge channelTypeId={c.id} />
									<p className="text-sm text-text">{c.displayName}</p>
								</div>
								<span className="text-xs text-bgBase-800">Demnächst verfügbar</span>
							</div>
						))}
					</div>
				</div>
			)}

			<ConfirmModal
				isOpen={Boolean(disableTarget)}
				onClose={() => setDisableTarget(null)}
				onConfirm={() => handleToggle(disableTarget, false, true)}
				title="Kanal deaktivieren"
				message={
					<>
						Möchten Sie den Kanal <strong>{disableTarget?.displayName}</strong> wirklich deaktivieren?
						<br />
						<br />
						Alle bestehenden Aktivitäten dieses Kanals bleiben erhalten. Es können lediglich keine neuen
						Aktivitäten über diesen Kanal erstellt werden.
					</>
				}
				confirmText="Deaktivieren"
				subtitle={null}
				isLoading={pendingId === disableTarget?.id}
			/>
		</div>
	);
};

export default ChannelsSettingsTab;
